import parseDuration from "parse-duration";
export const comparisonTypes = [
    "eq",
    "neq",
    "gte",
    "lte",
    "gt",
    "lt",
    "in",
    "contains",
    "not_contains",
    "starts",
    "ends_with",
    "is_null",
    "is_not_null",
    "is_older_than_duration",
    "is_newer_than_duration",
];
export const comparisonTypeText = {
    eq: "equals",
    neq: "does not equal",
    gte: "is greater than or equal to",
    lte: "is less than or equal to",
    gt: "is greater than",
    lt: "is less than",
    in: "is one of",
    contains: "contains",
    not_contains: "does not contain",
    starts: "starts with",
    ends_with: "ends with",
    is_null: "is unknown",
    is_not_null: "is any",
    is_older_than_duration: "is older than duration",
    is_newer_than_duration: "is newer than duration",
};
const baseOperations = {
    eq: (value, predicateValue) => value === predicateValue,
    neq: (value, predicateValue) => value !== predicateValue,
    in: (value, predicateValue) => predicateValue.split(",").includes(value?.toString()),
    gte: (value, predicateValue) => value >= predicateValue,
    lte: (value, predicateValue) => value <= predicateValue,
    gt: (value, predicateValue) => value > predicateValue,
    lt: (value, predicateValue) => value < predicateValue,
    is_null: (value) => value === null,
    is_not_null: (value) => value !== null,
    // these intended to be implemented by specific types
    contains: (value, predicateValue) => false,
    not_contains: (value, predicateValue) => false,
    starts: (value, predicateValue) => false,
    ends_with: (value, predicateValue) => false,
    is_older_than_duration: (value, predicateValue) => false,
    is_newer_than_duration: (value, predicateValue) => false,
};
const stringOperations = {
    contains: (value, predicateValue) => value !== null && value.includes(predicateValue),
    not_contains: (value, predicateValue) => value !== null && !value.includes(predicateValue),
    starts: (value, predicateValue) => value !== null && value.startsWith(predicateValue),
    ends_with: (value, predicateValue) => value !== null && value.endsWith(predicateValue),
};
const dateOperations = {
    eq: (value, predicateValue) => new Date(value) === new Date(predicateValue),
    neq: (value, predicateValue) => new Date(value) !== new Date(predicateValue),
    gte: (value, predicateValue) => new Date(value) >= new Date(predicateValue),
    lte: (value, predicateValue) => new Date(value) <= new Date(predicateValue),
    gt: (value, predicateValue) => new Date(value) > new Date(predicateValue),
    lt: (value, predicateValue) => new Date(value) < new Date(predicateValue),
    is_older_than_duration: (value, predicateValue) => {
        const duration = parseDuration(predicateValue);
        if (!duration || isNaN(duration))
            return false;
        return new Date(value) < new Date(Date.now() - duration);
    },
    is_newer_than_duration: (value, predicateValue) => {
        const duration = parseDuration(predicateValue);
        if (!duration || isNaN(duration))
            return false;
        return new Date(value) > new Date(Date.now() - duration);
    },
};
export function matchesCondition(condition, user) {
    const evaluatedPredicates = condition.predicates.map((predicate) => {
        const value = inferType(user[predicate.attribute]);
        const operationMap = (value) => {
            if (typeof value === "string") {
                return { ...baseOperations, ...stringOperations };
            }
            if (value instanceof Date) {
                return { ...baseOperations, ...dateOperations };
            }
            return baseOperations;
        };
        const op = operationMap(value)[predicate.comparison];
        const result = op(value, predicate.value);
        return {
            predicate,
            result,
            value: value === undefined ? "[undefined]" : value,
        };
    });
    const mainResult = {
        evaluatedPredicates,
        type: condition.type,
        result: evaluatedPredicates[condition.type === "all" ? "every" : "some"]((predicate) => predicate.result),
    };
    return mainResult;
}
export function inferType(value) {
    if (typeof value !== "string")
        return value;
    if (value === "true")
        return true;
    if (value === "false")
        return false;
    if (!isNaN(Number(value)))
        return Number(value);
    if (!isNaN(Date.parse(value)))
        return new Date(value);
    return value;
}
export function getGuideTargetingEvaluation(guide, mergedUserData) {
    const matchResult = guide.targeting?.who ? matchesCondition(guide.targeting.who, mergedUserData) : undefined;
    const targetingEvaluation = {
        isPublished: !!guide.published_at,
        isGenerated: guide.version === 2 ? !!guide.tour_playback : true,
        whoMatch: matchResult,
        isWhoTargeted: matchResult?.result ?? true,
    };
    return targetingEvaluation;
}
export function parseRoutePattern(path, currentUrl) {
    const currentPath = new URL(currentUrl).pathname;
    const currentRoutes = currentPath.split("/");
    return path
        .split("/")
        .map((r, index) => (r === "*" ? currentRoutes[index] : r))
        .join("/");
}
export function checkUrlCondition(urlCondition, currentUrl) {
    const parsedUrl = parseRoutePattern(urlCondition.right_operand, currentUrl);
    if (urlCondition.operator === "equals") {
        return parsedUrl === currentUrl;
    }
    else if (urlCondition.operator === "contains") {
        return currentUrl.includes(parsedUrl);
    }
    else if (urlCondition.operator === "not_contains") {
        return !currentUrl.includes(parsedUrl);
    }
    else if (urlCondition.operator === "starts_with") {
        return currentUrl.startsWith(parsedUrl);
    }
    else if (urlCondition.operator === "ends_with") {
        return currentUrl.endsWith(parsedUrl);
    }
    return false;
}
export function calculateLocalTimeConditionDelta(localTimeCondition, localTime) {
    const targetedLocalTime = new Date(Date.parse(localTimeCondition.right_operand));
    const localTotalSeconds = localTime.getHours() * 3600 + localTime.getMinutes() * 60;
    const targetTotalSeconds = targetedLocalTime.getHours() * 3600 + targetedLocalTime.getMinutes() * 60;
    return targetTotalSeconds - localTotalSeconds;
}
export function calculateTimeoutOnCurrentPageConditionDelta(timeonCurrentPageCondition, firstTimeVisitedAt, currentDate) {
    if (timeonCurrentPageCondition.operator === "after") {
        const timeDifference = currentDate.getTime() - firstTimeVisitedAt.getTime();
        return timeDifference;
    }
    return 0;
}
